import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { MessageCircle, Loader2, Trash2, Plus, Send, Users, User, FileText, Film } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const REPORT_SECTIONS = [
  { key: "cobranzas", label: "1. Cobranzas del mes" },
  { key: "cuentas", label: "2. Cuentas, líneas y deudas vencidas" },
  { key: "emitidos", label: "3. Pagarés emitidos" },
  { key: "pendientes", label: "4. Pagarés pendientes de firma" },
];

export default function WhatsAppModal({ vendedor, vendedores = [], fechaCorte }) {
  const [open, setOpen] = useState(false);
  const [contactos, setContactos] = useState([]);
  const [difusion, setDifusion] = useState([]);
  const [mode, setMode] = useState("vendedor"); // vendedor | difusion
  const [includePdf, setIncludePdf] = useState(true);
  const [includeVideo, setIncludeVideo] = useState(false);
  const [sections, setSections] = useState(REPORT_SECTIONS.map((s) => s.key));
  const [splitSections, setSplitSections] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const allSelected = sections.length === REPORT_SECTIONS.length;
  const toggleSection = (key) =>
    setSections((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const toggleAllSections = () =>
    setSections(allSelected ? [] : REPORT_SECTIONS.map((s) => s.key));


  // quick-add forms
  const [cVend, setCVend] = useState("");
  const [cTel, setCTel] = useState("");
  const [dNom, setDNom] = useState("");
  const [dTel, setDTel] = useState("");

  const load = async () => {
    const [c, d] = await Promise.all([api.get("/contactos"), api.get("/difusion")]);
    setContactos(c.data);
    setDifusion(d.data);
  };
  useEffect(() => { if (open) load(); }, [open]);
  useEffect(() => { if (vendedor && vendedor !== "__all__") setCVend(vendedor); }, [vendedor]);

  const addContacto = async () => {
    if (!cVend.trim() || !cTel.trim()) return toast.error("Complete vendedor y teléfono");
    await api.post("/contactos", { vendedor: cVend.trim(), telefono: cTel.trim() });
    setCTel(""); toast.success("Contacto agregado"); load();
  };
  const delContacto = async (id) => { await api.delete(`/contactos/${id}`); load(); };
  const addDifusion = async () => {
    if (!dNom.trim() || !dTel.trim()) return toast.error("Complete nombre y teléfono");
    await api.post("/difusion", { nombre: dNom.trim(), telefono: dTel.trim() });
    setDNom(""); setDTel(""); toast.success("Agregado a la lista"); load();
  };
  const delDifusion = async (id) => { await api.delete(`/difusion/${id}`); load(); };

  const targetVendedor = vendedor && vendedor !== "__all__" ? vendedor : null;
  const telefonos = mode === "difusion"
    ? difusion.map((d) => d.telefono)
    : contactos.filter((c) => !targetVendedor || c.vendedor === targetVendedor).map((c) => c.telefono);

  const send = async () => {
    if (telefonos.length === 0) return toast.error("No hay números de destino para este envío");
    if (!includePdf && !includeVideo && !message.trim()) return toast.error("Incluya un mensaje, PDF o video");
    if (includePdf && sections.length === 0) return toast.error("Seleccione al menos una sección del informe");
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/whatsapp/send", {
        vendedor: vendedor || "__all__",
        message,
        include_pdf: includePdf,
        include_video: includeVideo,
        sections: allSelected ? null : sections,
        split_sections: splitSections,
        telefonos,
        origin: window.location.origin,
        fecha_corte: fechaCorte,
      });
      setResult(data);
      if (data.status === "media_only") {
        toast.warning("Archivos generados. Twilio no está configurado para enviar.");
      } else {
        const enviados = data.enviados || 0;
        const total = telefonos.length;
        if (enviados === total) toast.success(`Enviado a ${enviados}/${total} número(s)`);
        else if (enviados > 0) toast.warning(`Enviado a ${enviados}/${total}. Algunos números fallaron.`);
        else {
          const err = data.resultados?.find((r) => !r.ok)?.error || "Verifica la configuración de Twilio y el número destino.";
          toast.error(`No se pudo enviar. ${err}`);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="whatsapp-modal-trigger" variant="outline" className="h-9 border-emerald-200 text-emerald-800 hover:bg-emerald-50">
          <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-emerald-700" /> Enviar informe por WhatsApp
          </DialogTitle>
          <DialogDescription>
            Adjunta automáticamente un PDF del informe y/o un video resumen (MP4) del mes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {/* Modo */}
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="mode-vendedor" onClick={() => setMode("vendedor")}
              className={`rounded-lg border p-3 text-left ${mode === "vendedor" ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
              <div className="flex items-center gap-2 font-semibold text-sm"><User className="h-4 w-4" /> Individual (vendedor)</div>
              <div className="text-[11px] text-slate-500 mt-1">
                {targetVendedor ? `Enviar a los contactos de ${targetVendedor}` : "Enviar a los contactos (filtre un vendedor para acotar)"}
              </div>
            </button>
            <button data-testid="mode-difusion" onClick={() => setMode("difusion")}
              className={`rounded-lg border p-3 text-left ${mode === "difusion" ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
              <div className="flex items-center gap-2 font-semibold text-sm"><Users className="h-4 w-4" /> Grupo / Difusión</div>
              <div className="text-[11px] text-slate-500 mt-1">Difusión a la lista de números del grupo</div>
            </button>
          </div>

          {/* Adjuntos */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Adjuntos</div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm"><FileText className="h-4 w-4 text-red-600" /> Informe en PDF</span>
              <Switch data-testid="toggle-pdf" checked={includePdf} onCheckedChange={setIncludePdf} />
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm"><Film className="h-4 w-4 text-emerald-700" /> Video resumen (MP4)</span>
              <Switch data-testid="toggle-video" checked={includeVideo} onCheckedChange={setIncludeVideo} />
            </div>
          </div>

          {/* Secciones del informe PDF */}
          {includePdf && (
            <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="wa-sections-block">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Contenido del informe</div>
                <button data-testid="wa-sections-toggle-all" onClick={toggleAllSections}
                  className="text-[11px] font-medium text-emerald-700 hover:underline">
                  {allSelected ? "Quitar todas" : "Seleccionar todas"}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {REPORT_SECTIONS.map((s) => (
                  <label key={s.key} htmlFor={`wa-sec-${s.key}`}
                    className="flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-2 text-sm cursor-pointer hover:bg-slate-50">
                    <Checkbox id={`wa-sec-${s.key}`} data-testid={`wa-section-${s.key}`}
                      checked={sections.includes(s.key)} onCheckedChange={() => toggleSection(s.key)} />
                    <span>{s.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between border-t border-slate-100 pt-2.5">
                <span className="text-sm text-slate-600">Enviar cada sección como archivo separado</span>
                <Switch data-testid="wa-split-sections" checked={splitSections} onCheckedChange={setSplitSections} />
              </div>
              <div className="text-[11px] text-slate-400">
                {sections.length === 0
                  ? "Selecciona al menos una sección."
                  : splitSections && sections.length > 1
                  ? `Se generarán ${sections.length} PDFs separados (uno por sección).`
                  : "Se generará un único PDF con las secciones elegidas."}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Mensaje</Label>
            <Textarea data-testid="wa-message" rows={2} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Estimado equipo, adjunto el informe de gestión de cartera del mes..." />
          </div>

          {/* Gestión de contactos */}
          {mode === "vendedor" ? (
            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Teléfonos por vendedor</div>
              <div className="flex gap-2">
                <Input list="vend-list" value={cVend} onChange={(e) => setCVend(e.target.value)} placeholder="Vendedor" className="h-9" data-testid="contacto-vendedor" />
                <datalist id="vend-list">{vendedores.map((v) => <option key={v} value={v} />)}</datalist>
                <Input value={cTel} onChange={(e) => setCTel(e.target.value)} placeholder="+595981123456" className="h-9" data-testid="contacto-telefono" />
                <Button onClick={addContacto} className="h-9 bg-emerald-700 hover:bg-emerald-800" data-testid="add-contacto"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {contactos.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2 py-1">
                    <span><strong>{c.vendedor}</strong> · {c.telefono}</span>
                    <button onClick={() => delContacto(c.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {contactos.length === 0 && <div className="text-xs text-slate-400">Sin contactos aún.</div>}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Lista de difusión (grupo)</div>
              <div className="flex gap-2">
                <Input value={dNom} onChange={(e) => setDNom(e.target.value)} placeholder="Nombre" className="h-9" data-testid="difusion-nombre" />
                <Input value={dTel} onChange={(e) => setDTel(e.target.value)} placeholder="+595981123456" className="h-9" data-testid="difusion-telefono" />
                <Button onClick={addDifusion} className="h-9 bg-emerald-700 hover:bg-emerald-800" data-testid="add-difusion"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {difusion.map((d) => (
                  <div key={d.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2 py-1">
                    <span><strong>{d.nombre}</strong> · {d.telefono}</span>
                    <button onClick={() => delDifusion(d.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {difusion.length === 0 && <div className="text-xs text-slate-400">Sin números en la lista.</div>}
              </div>
            </div>
          )}

          <div className="text-xs text-slate-500">
            Se enviará a <strong>{telefonos.length}</strong> número(s).
          </div>

          {result?.media?.length > 0 && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm">
              <div className="font-semibold text-emerald-800 mb-1">Archivos generados:</div>
              {result.media.map((m) => (
                <a key={m.id} href={m.url} target="_blank" rel="noreferrer" className="block text-emerald-700 underline text-xs" data-testid={`media-link-${m.tipo}`}>
                  {m.tipo === "pdf" ? "📄" : "🎬"} {m.filename}
                </a>
              ))}
              {result.warning && <div className="text-amber-700 text-xs mt-2">{result.warning}</div>}
              {result.resultados?.length > 0 && (
                <div className="mt-2 space-y-0.5" data-testid="wa-send-results">
                  {result.resultados.map((r, i) => (
                    <div key={i} className={`text-xs ${r.ok ? "text-emerald-700" : "text-red-600"}`}>
                      {r.ok ? "✅" : "⚠️"} {r.telefono}{r.ok ? " · enviado" : ` · ${r.error}`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <Button onClick={send} disabled={sending} className="w-full h-11 bg-emerald-700 hover:bg-emerald-800" data-testid="wa-send-button">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Generar y enviar por WhatsApp
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
